import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import ellipsis from 'ellipsis'
import { EPSILON } from 'chart-constants'
import * as colors from 'colors'

const verticalPadding = 5
const minHeight = 15

const borders = (selection, stroke, transition) => {
  selection.each(function (data) {
    let serie = d3.select(this.parentNode).datum()
      , color = data.color || serie.color
      , lines = d3.select(this)
                  .selectAll('line.border')
                  .data(d => {
                    if (!stroke) {
                      return [] // If the stroke is set to 0, we don't want to draw the elements
                    }

                    let borders = ['left', 'right'] // Always have a left and right border

                    if (!d.raw) {
                      // Doesn't have a value, so we don't honor it being the top/bottom. The bar
                      // must have substance to have top/bottom borders.
                      return borders
                    }

                    if (d._topStack) {
                      borders.push('top') // top border
                    }
                    if (d._bottomStack) {
                      borders.push('bottom') //  border
                    }

                    return borders
                  }, d => d)
      , enter = lines.enter()
                     .append('line')
                     .attr('class', 'border')
                     .style('stroke-width', d => {
                       if (transition.duration && transition.duration()) {
                         // If there's a transition, we want to fade in top/bottom
                         return ((d === 'top' || d === 'bottom') ? EPSILON : stroke) + 'px'// Side borders don't fade
                       } else {
                         return `${stroke}px`
                       }
                     })
      , exit = lines.exit()

    enter.merge(lines)
         .style('stroke', d => colors.mixBlack(colors.lookupObj(color).rgba, .12))
         .transition(transition)
         .style('stroke-width', `${stroke}px`)

    exit.classed('border-exit', true)
        .transition(transition)
        .style('stroke-width', `${EPSILON}px`) // Fade out the top and bottom
        .remove()
  })
}

const borderPosition = (selection, dim, stroke) => {
  selection.each(function () {
    let borders = d3.select(this)
                    .selectAll('line.border')
                    .filter(function () {
                      // If the border is marked as being removed, ignore it
                      return !d3.select(this).classed('border-exit')
                    })
      , buffer = .1 // Add a small adjustment of .1 to make sure the borders look like they are touching

    d3.select(this)
      .selectAll('line.border')
      .attr('x1', position => {
        let offset = 0
        if (position === 'top' || position === 'bottom') {
          // We need to offset based on half the left stroke width
          offset = stroke / 2
        }
        return (position === 'right' ? dim.x + dim.width : dim.x) - offset
      })
      .attr('x2', position => {
        let offset = 0

        if (position === 'top' || position === 'bottom') {
          offset = stroke / 2
        }

        return (position === 'left' ? dim.x : dim.x + dim.width) + offset
      })
      .attr('y1', position => {
        let top = borders.filter(d => d === 'top')

        if ((position === 'left' || position === 'right') && top.size()) {
          // Drawing side borders and we have a top border, so we need to offset the side by half the stroke-width
          let stroke = parseFloat(top.style('stroke-width')) // Current stroke as it's transitioning

          return dim.y + stroke / 2 - buffer
        } else if (position === 'bottom') {
          return dim.y + dim.height
        } else {
          return dim.y
        }
      })
      .attr('y2', position => {
        let bottom = borders.filter(d => d === 'bottom')

        if ((position === 'left' || position === 'right') && bottom.size()) {
          // Drawing side borders and we have a bottom border, so we need to offset the side by half the stroke-width
          let stroke = parseFloat(bottom.style('stroke-width'))

          return dim.y + dim.height - stroke / 2 + buffer
        } else if (position === 'top') {
          return dim.y // flat line
        } else {
          return dim.y + dim.height
        }
      })
  })
}

const labels = (selection, transition) => {
  selection.each(function (data) {
    let serie = d3.select(this.parentNode).datum()
      , label = d3.select(this)
                  .selectAll('g.text-group')
                  .data(d => {
                    let labels = []

                    if (!d.formatted) {
                      return labels
                    }

                    if (serie.stackBy) {
                      // When stacked we combine the title and value into the
                      // title component
                      let text = ''

                      if (d.formatted.title) {
                        text += d.formatted.title
                      }
                      if (d.formatted.value) {
                        if (text.length) {
                          text += ' ' // Two components -- Add a space between them
                        }
                        text += d.formatted.value
                      }
                      if (text.length) {
                        labels.push({
                          text,
                          clazz: 'title'
                        })
                      }
                    } else {
                      if (d.formatted.value) {
                        // If it's stacked, we append the value to the title
                        labels.push({
                          text: d.formatted.value,
                          clazz: 'subtitle'
                        })
                      }

                      if (d.formatted.title) {
                        labels.push({
                          text: d.formatted.title,
                          clazz: 'title'
                        })
                      }
                    }

                    return labels
                  })
                  .join('g')
                  .attr('class', d => `text-group-${d.clazz} text-group`)
                  .selectAll('text')
                     .data(d => [d])
      , enter = label.enter()
                     .append('text')
                     .style('opacity', EPSILON)
      , exit = label.exit()

    enter.merge(label)
         .text(d => d.text)
         .attr('class', d => {
            // Determine the fill of the rect since it's been adjusted slightly
            // Use it to determine the text variation on the bar
            let fill = d3.select(this)
                         .selectAll('rect')
                         .style('fill')
            return `bar-label label-on-colored-background ${d.clazz} ${colors.getTextVariation(fill)}`
          })
         .transition(transition)
         .style('opacity', 1)

    exit.transition(selection)
        .style('opacity', EPSILON) // Fade out the top and bottom
        .remove()
  })
}

const valueLabelPosition  = (selection, svg, plots, dim, serie, labelsConfig) => {
  let vertical = labelsConfig.longestValueBB.width > dim.width

  selection.each(function (d) {
    let above = labelsConfig.showingTitle ||
      (vertical && labelsConfig.longestValueBB.width > dim.height) ||
      (!labelsConfig.showingTitle && !vertical && labelsConfig.longestValueBB.height > dim.height)

    d3.select(this)
      .select('g.text-group-subtitle')
      .attr('transform', function () {
        let x = dim.x + dim.width / 2

        if (vertical) {
          let y = dim.y + (verticalPadding * (above ? -1 : 1))
          x -= 1
          return `translate(${x}, ${y}) rotate(-90)`
        } else {
          // Horizontal layout
          let y = 0

          if (above) {
            y = dim.y - verticalPadding
          } else {
            y = dim.y + labelsConfig.longestValueBB.height - verticalPadding
          }

          return `translate(${x}, ${y}) rotate(0)`
        }
      })
      .select('text.subtitle')
      .classed('vertical', vertical)
      .classed('horizontal', !vertical)
      .classed('above', above)
      .style('display', function () {
        return vertical && labelsConfig.longestValueBB.height - 5 > dim.width ? 'none' : ''
      })
      .select(function () {
        let clazzes = this.classList
        // If the label is above the chart, we always want dark text styling
        if (above) {
          clazzes.remove('light')
          clazzes.add('dark')
        }
      })
  })
}

const titleLabelPosition = (selection, svg, plots, dim, serie, labelsConfig) => {
  let vertical = !serie.stackBy // All non-stacked are vertical aligned

  if (serie.stackBy && labelsConfig.longestTitleBB.width > dim.width) {
    // The stacked by title is too wide, flip it
    vertical = true
  }

  let ellipsisWidth = 0

  if (serie.stackBy) {
    ellipsisWidth = vertical ? (dim.height - verticalPadding) : dim.width
  } else {
    // It's vertical so available space is based on height
    ellipsisWidth = dim.height - verticalPadding
  }

  selection.each(function (d) {
    d3.select(this)
      .select('g.text-group-title')
      .attr('transform', d => {
        let x = dim.x + dim.width / 2
        if (serie.stackBy) {
          // Stacked. center it
          let y = (dim.height / 2) + dim.y //+ verticalPadding

          if (!verticalPadding) {
            y += verticalPadding
          }
          return `translate(${x}, ${y}) ${vertical ? 'rotate(-90)' : ''}`
        } else {
          let y = dim.height + dim.y - verticalPadding
          return `translate(${x}, ${y}) rotate(-90)`
        }

      })
      .select('text.title')
      .classed('vertical', vertical)
      .classed('stack-by', serie.stackBy)
      .call(ellipsis, ellipsisWidth)
      .style('display', function () {
        if (serie.stackBy && dim.height < minHeight) {
          return 'none' // Not enough room
        }
        return dim.width < minHeight ? 'none' : '' // Not enough space to show the label, hide it
      })

  })
}

const labelPosition = (selection, dim, serie, labelsConfig) => {
  let svg = selection.node().ownerSVGElement
    , plots = d3.select(svg).datum().plots

  selection.call(titleLabelPosition, svg, plots, dim, serie, labelsConfig)
           .call(valueLabelPosition, svg, plots, dim, serie, labelsConfig)
}

export default function (selection, stroke) {
  selection.each(function (data) {
    d3.select(this)
      .call(borders, stroke, selection)
      .call(labels, selection)
  })
}

export const position = function (selection, stroke, dimensions = {}, labelsConfig) {
  selection.each(function (data) {
    let container = d3.select(this)
      , rect = container.select('rect')
      , dim = Object.assign({
        x: +rect.attr('x'),
        width: +rect.attr('width'),
        y: +rect.attr('y'),
        height: +rect.attr('height')
      }, dimensions)
      , serie = d3.select(this.parentNode).datum()

    container.call(borderPosition, dim, stroke)
             .call(labelPosition, dim, serie, labelsConfig)
  })
}
